/**
 * Claimed intake: the FILE contract of a draft (docs/29 §Statement upload, crossmarc/steward-app#1013,
 * #2307, #2310).
 *
 * A prospect who has claimed their draft may attach the files an advisor asks for at a first
 * meeting. This module holds the values the processes involved must agree on, and nothing else:
 *
 *   - the ALLOW-LIST of content types, the per-file byte cap and the per-draft count cap, read by
 *     the writer (which stores the reference), by kept-link (which bounds what it writes), by the
 *     reader and façade, and by the SPA (which refuses a file before it is sent);
 *   - the schema for the REFERENCE that rides the draft row as `documents`
 *     ({@link IntakeDraftDocumentRefSchema}): the bytes are an appcore client document, never
 *     stored under this contract.
 *
 * ## What is NOT here (removed in #2310)
 *
 * There is no object store behind a draft any more: no bucket name, no object key and no record
 * schema for an uploaded object. A stored draft or intake row written before #2308 PR 2 may still
 * carry a legacy `statements` array; no schema here describes it, no reader looks at it, and it
 * goes when the row does. Nothing in THIS package pins that: the readers live elsewhere, and each
 * has its own test with a row carrying the array (the array has no effect, the row still reads):
 *
 *   - person's draft, read side: `services/intake-reader/src/__tests__/reader.test.ts`
 *     ("legacy" read beside a clean one, same keys), `linkContext.test.ts` and
 *     `ownerBinding.test.ts` (a draft holding both `statements` and `documents`);
 *   - person's draft, write side: `services/intake-writer/src/__tests__/draft.test.ts`, "a row
 *     still carrying a legacy `statements` array is saved, and the array rides along unread and
 *     unchanged" (pins the save path only; the other draft updates in `draft.ts`, as read on
 *     2026-10-13, also build the row from `...stored.attributes`, so the array rides along there
 *     too, but no test pins those);
 *   - firm's intake row: `services/steward-app-api/src/__tests__/advisor.test.ts`, "a legacy
 *     `statements` attribute is not sent and not read" (detail and list);
 *   - retention sweeper: `services/intake-retention/src/__tests__/noDocumentStore.test.ts`
 *     (draft and intake rows carrying the array are swept like any other);
 *   - client purge: `services/intake-retention/src/__tests__/purge.test.ts` (a draft row and a
 *     submission carrying the array are purged exactly like one without).
 *
 * The inverse, that a save MESSAGE carrying `statements` is refused, is `intakeStatements.test.ts`
 * in this package.
 *
 * {@link INTAKE_STATEMENT_MAX_PER_DRAFT} is enforced by the reader before it mints and by the
 * writer's attach, both counting `documents`; {@link INTAKE_STATEMENT_MAX_BYTES} bounds what the
 * SPA sends and what kept-link writes (appcore applies its own limit,
 * `CLIENT_DOCUMENT_MAX_BYTES` in `@steward-app/appcore-system`).
 *
 * 🚨 **`size` on a reference is a display value** ("2.1 MB"). Nothing downstream may bill,
 * allocate, or trust a buffer size from it.
 */

import { z } from 'zod';
import { ULID_PATTERN } from './intakeMessage.js';
import { IntakeRowIdSchema } from './intakeRowId.js';
import { IntakeSnapshotAccountIdSchema } from './intakeSnapshot.js';

/**
 * The content types a prospect may upload, and the ONLY ones.
 *
 * docs/29 §Statement upload named "PDF, CSV, common images"; CSV has since been dropped (below).
 * This is the list, spelled as
 * the exact `Content-Type` strings the (since deleted, #2307) presigned POST's `eq` condition
 * compared against, and the same strings the SPA declares on its PUT —
 * a set, not a prefix rule, because `image/*` would admit `image/svg+xml`, which is a
 * script-bearing document that a browser will execute if it is ever served inline.
 *
 * 🚨 **Widening this list is a security change, not a convenience one:** every addition has to
 * survive the question "what happens when an advisor opens this in a browser tab". No route
 * in this repository serves an uploaded draft file (the advisor download
 * was deleted in crossmarc/steward-app#2309); a firm opens a client's file through the client
 * document route, where appcore applies its own scan gate.
 *
 * CSV is NOT on the list (founder decision, crossmarc/steward-app#2293; removed in #2307). It was
 * admitted before as a named residual (formula injection when an adviser opens a bank export
 * in a spreadsheet, #1192), and appcore's client document routes do not accept it, so the
 * residual no longer has a way in through this channel. The remaining three are the types a
 * Plaid statement record can carry: every Plaid statement is a PDF
 * (`PLAID_STATEMENT_CONTENT_TYPE` in `services/kept-link/src/plaidSnapshot.ts`, read
 * 2026-10-12), and the two image types are what the old browser upload path wrote.
 */
export const INTAKE_STATEMENT_CONTENT_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
] as const;

export type IntakeStatementContentType = (typeof INTAKE_STATEMENT_CONTENT_TYPES)[number];

/** The allow-list as a zod enum, for the two schemas and the route's request shape. */
export const IntakeStatementContentTypeSchema = z.enum(INTAKE_STATEMENT_CONTENT_TYPES);

/**
 * The per-file byte cap: 25MB, the figure docs/29 §Statement upload and #1013 both name.
 *
 * Was enforced by the presigned POST's `content-length-range` condition, which S3 evaluated
 * against the request S3 actually received (that POST was deleted in #2307; see the header). It
 * is still the figure the SPA refuses against. A bank's twelve-month PDF statement is
 * a few megabytes; 25MB is generous for a scan of a paper one.
 */
export const INTAKE_STATEMENT_MAX_BYTES = 25 * 1024 * 1024;

/**
 * The smallest upload worth minting for. Zero-byte objects are a way to spend the count
 * cap without transferring anything, and an empty statement is not a statement.
 */
export const INTAKE_STATEMENT_MIN_BYTES = 1;

/** The per-draft count cap: 20, the figure docs/29 and #1013 name. See the header on why
 *  this is enforced in two places and the byte cap in a third. */
export const INTAKE_STATEMENT_MAX_PER_DRAFT = 20;

/**
 * The longest display name a statement record may carry.
 *
 * 🚨 **The name is caller text and it never reaches an object key, a log line or a path.**
 * No key is built from it, which is
 * what makes docs/29's "filenames never appear in logs — the zero-caller-text discipline
 * extends to object keys derived from user input" structurally true rather than carefully
 * maintained. What the name IS for is showing a person which of their own files is which,
 * on their page.
 */
export const INTAKE_STATEMENT_NAME_MAX = 120;

/**
 * The charset a display name may use: anything but control characters and the two
 * noncharacters SQS rejects.
 *
 * Deliberately permissive about letters -- a statement from a Quebecois or Taiwanese bank
 * has a name in its own script and mangling it helps nobody -- and deliberately strict about
 * the characters that break the things this value passes through: C0 and C1 control
 * characters (a newline forges a JSON log line and a CSV row) and the two noncharacters
 * `intakeSchema.ts`'s own `SQS_SAFE` excludes because SQS refuses a message carrying them.
 */
const STATEMENT_NAME_SAFE = /^[^\u0000-\u001F\u007F-\u009F\uFFFE\uFFFF]+$/u;

/**
 * Which intake section a file belongs to: cash, investments, or tax returns (added 2026-10-07).
 * A closed enum, not a free string, so a new section is a schema change rather than a label a
 * client happens to send. A tax return is stored exactly as a statement is, in the same array,
 * so it counts against the same per-draft cap ({@link INTAKE_STATEMENT_MAX_PER_DRAFT}).
 * Declared before {@link IntakeDraftDocumentRefSchema}, whose object literal reads it at module load.
 */
export const IntakeStatementSectionSchema = z.enum(['cash', 'investments', 'taxReturns']);

export type IntakeStatementSection = z.infer<typeof IntakeStatementSectionSchema>;

/**
 * The content types a client document accepts once a draft file is a `firm-client-document`
 * row in appcore's documents bucket (crossmarc/steward-app#2293, #2306): appcore's
 * `ONBOARDING_CLIENT_DOCUMENT_MIME_TYPES`, copied from appcore `fa57c65` on 2026-10-12. CSV is
 * not among them (founder decision, #2293). A COPY: `intakeStatements.test.ts` compares
 * it to a literal of that list, so it goes red when this copy is edited, not when appcore moves
 * and not when `CLIENT_DOCUMENT_MIME_TYPES` in `@steward-app/appcore-system` drifts from it (this
 * package has no dependency on that one, so no test ties the two).
 *
 * This is the type list for a file the person adds (#2307). {@link INTAKE_STATEMENT_CONTENT_TYPES}
 * is the wider list the Plaid record still uses; it no longer
 * admits CSV either.
 */
export const INTAKE_DOCUMENT_CONTENT_TYPES = ['application/pdf', 'image/jpeg', 'image/png'] as const;

/**
 * ONE file on a draft, as a REFERENCE to the appcore document that holds it (#2306, slice 1 of
 * #2293). It carries a `documentId` and nothing else that decides access: the bytes live in
 * appcore, not under a key built from this record.
 *
 * Stored on the draft row as `documents` (#2307).
 *
 * 🚨 **`documentId` is recorded SERVER-SIDE and must never be taken from a browser's save.** As
 * a firm-wide id it would let a prospect attach another client's document to their own draft.
 * This schema cannot tell the two apart (it validates shape), so the guarantee belongs to
 * whichever process writes the record, and to the messages that reach it: neither the `save`
 * message nor the `document-attach` message has a `documentId` or `source` key
 * (`intakeDraft.test.ts` pins both refusals). The intake writer builds the reference from the
 * upload ledger's own key.
 *
 * `source` is appcore's own vocabulary (`client`: the person uploaded it; `plaid`: pulled from a
 * connected institution), NOT the `upload` the statement schema accepted before #2307. It is
 * REQUIRED here: the old "absent means upload" existed for rows written before #1014, and a
 * reference has no such rows.
 * `section` is required for the same reason. `accountId` and `rowId` keep their meaning and the
 * either-or rule.
 */
export const IntakeDraftDocumentRefSchema = z.strictObject({
  documentId: z.string().regex(ULID_PATTERN),
  section: IntakeStatementSectionSchema,
  accountId: IntakeSnapshotAccountIdSchema.optional(),
  rowId: IntakeRowIdSchema.optional(),
  // At most 120 characters, while appcore accepts 300 and returns the sanitized name cut to 200: whoever
  // records the reference must bound the name BEFORE the mint, because a reference that fails to
  // parse after a successful commit leaves an orphan document (#2306 review, instruction to #2307).
  name: z.string().trim().min(1).max(INTAKE_STATEMENT_NAME_MAX).regex(STATEMENT_NAME_SAFE),
  contentType: z.enum(INTAKE_DOCUMENT_CONTENT_TYPES),
  size: z.number().int().min(INTAKE_STATEMENT_MIN_BYTES).max(INTAKE_STATEMENT_MAX_BYTES),
  uploadedAt: z.iso.datetime().max(64),
  source: z.enum(['client', 'plaid']),
}).refine((record) => record.rowId === undefined || record.accountId === undefined, {
  message: 'A document belongs to a typed row or to a connected account, not both.',
  path: ['rowId'],
});

export type IntakeDraftDocumentRef = z.infer<typeof IntakeDraftDocumentRefSchema>;

/**
 * The `documents` attribute of a draft row. Capped at {@link INTAKE_STATEMENT_MAX_PER_DRAFT}. Since
 * crossmarc/steward-app#2308 PR 2 the cap that binds a person counts THIS list only (the legacy
 * `statements` list is no longer written and takes no room); the reader's mint, `linkContext`'s
 * headroom and the writer's attach all count `documents` alone.
 *
 * Not carried by any queue message: the `save` message has no `documents` key, so the only
 * writer of this attribute is the intake writer acting on `document-attach` and `remove`.
 */
export const IntakeDraftDocumentsSchema = z.array(IntakeDraftDocumentRefSchema)
  .max(INTAKE_STATEMENT_MAX_PER_DRAFT);

export type IntakeDraftDocuments = z.infer<typeof IntakeDraftDocumentsSchema>;
